import React from 'react';
import { Stack, Button, Input, Text, AnchorTextLink } from '@chaser/components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Totp from '../Totp';
import CardSection from '../../../../components/CardSection';
import useFetch from '../../../../hooks/useFetch';
import AccountService from '../../../../services/account-service';
import LabeledFieldWithErrorLabel from '../../../../components/LabeledFieldWithErrorLabel/index';
import usePush from '../../../../hooks/usePush';
import AuthenticationService from '../../../../services/authentication-service';
import { checkPasswordStrength } from '../../../../util/passwordStrength';
import { useDispatch } from 'react-redux';
import { setUser } from '../../../login/duck/actions/login';
import useUser from '@/hooks/useUser';

const Security = () => {
  const {
    data: { hasTOTP },
    refetch,
  } = useFetch(AccountService.getUserInfo);
  const dispatch = useDispatch();

  const { execute: savePassword, loading } = usePush(AuthenticationService.updateExistingPassword, {
    successToast: 'Password changed',
    errorToast: true,
  });

  const { isExternalAuthentication } = useUser();

  return (
    <Stack>
      {isExternalAuthentication ? (
        <CardSection title="Single Sign-On (SSO) Authentication">
          <Text>
            You’re currently signing in using Single Sign-On. To change your password or update your
            security settings, please contact your provider.
          </Text>
        </CardSection>
      ) : (
        <>
          <CardSection title="Change password">
            <Formik
              initialValues={{
                currentPassword: '',
                newPassword: '',
              }}
              validationSchema={Yup.object().shape({
                currentPassword: Yup.string().required('You must enter your current password!'),
                newPassword: Yup.string()
                  .required('Password required!')
                  .max(100)
                  .test(
                    'checkPasswordStrength',
                    'Password too weak. Try uppercase letters, symbols and numbers.',
                    checkPasswordStrength(3, 10),
                  ),
              })}
              validateOnBlur={false}
              onSubmit={async ({ currentPassword, newPassword }, actions) => {
                const [err, res] = await savePassword({
                  currentPassword,
                  newPassword,
                });

                const { user, token } = res;
                dispatch(setUser({ ...user, token }));
                actions.setSubmitting(false);
                if (err) return;
                actions.resetForm();
              }}
            >
              {({ values }) => (
                <Form>
                  <Stack gap="small">
                    <Stack gap="small">
                      <LabeledFieldWithErrorLabel
                        label="Current password"
                        name="currentPassword"
                        as={Input}
                        placeholder="Enter current password"
                        type="password"
                        autocomplete="off"
                      />
                    </Stack>
                    <Stack gap="small">
                      <LabeledFieldWithErrorLabel
                        label="New password"
                        name="newPassword"
                        as={Input}
                        placeholder="Enter new password"
                        type="password"
                        autocomplete="off"
                      />
                    </Stack>
                    <div>
                      <Button
                        disabled={!(values.currentPassword && values.newPassword)}
                        type="submit"
                        loading={loading}
                        appearance="secondary"
                      >
                        Save password
                      </Button>
                    </div>
                  </Stack>
                </Form>
              )}
            </Formik>
          </CardSection>
          <CardSection
            title="Two-Factor Authentication (2FA)"
            buttons={<Totp enabled={hasTOTP} refetch={refetch} />}
          >
            {hasTOTP ? (
              <Text>
                Two-Factor Authentication is active on your account. <br />
                To change the registered device, first remove 2FA and then setup again with the new
                device.
              </Text>
            ) : (
              <Text>
                2FA is a type of multi-factor authentication that provides an added layer of
                security to your account when logging in.
                <br />
                When you log into Chaser, you have to enter a unique code generated by a mobile
                authentication app like{' '}
                <AnchorTextLink href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en">
                  Google Authenticator
                </AnchorTextLink>{' '}
                to gain access.
              </Text>
            )}
          </CardSection>
        </>
      )}
    </Stack>
  );
};

export default Security;
